/**
 * useExperiment — A/B/N variant resolver with two-tier strategy.
 *
 *   const { variant, copy, ready, recordClick } = useExperiment("landing-hero", {
 *     tier:    "default",
 *     source:  "landing-hero",
 *     fallback: { label: "...", headline: "...", cta: "..." },
 *   });
 *
 * Resolution order:
 *   1. PostHog feature flag (when REACT_APP_POSTHOG_KEY is set)
 *      • Variant: read via posthog.getFeatureFlag(experiment).
 *      • Copy:    read via posthog.getFeatureFlagPayload(experiment) — JSON
 *                  payload set in the PostHog UI per variant. Shape:
 *                    { label: "...", headline: "...", cta: "..." }
 *      • Exposure: PostHog auto-fires `$feature_flag_called` on read, AND
 *                  we fire our `experiment_view` event with {experiment,
 *                  variant} for use in the same funnel as the rest of our
 *                  analytics. recordClick fires `experiment_click`.
 *   2. Server-pinned variant (legacy nudge harness)
 *      • Used when PostHog is dormant OR the flag is unset/false in PostHog.
 *      • Calls GET /api/nudge/{experiment}?tier=...; cookie-pins per visitor.
 *      • Logs view/click via POST /api/nudge/{experiment}/event.
 *
 * Callers don't need to know which tier resolved them — the public API
 * (variant/copy/ready/recordClick) is identical.
 *
 * Defensive: every analytics call swallows errors so a Resend or Mongo
 * blip never breaks the surface for the visitor.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "./api";
import { getInstance, isPostHogEnabled } from "./posthog";
import { trackEvent } from "./analytics";

export default function useExperiment(experiment, opts = {}) {
  const {
    tier      = "default",
    source    = experiment,
    fallback  = { label: "", headline: "", cta: "" },
    extraEventFields = {},
    viewWhen  = true,
  } = opts;

  const [variant, setVariant] = useState("v1");
  const [copy,    setCopy]    = useState(fallback);
  const [ready,   setReady]   = useState(false);
  const [resolvedVia, setResolvedVia] = useState(null); // "posthog" | "server" | null
  const viewLoggedRef = useRef(false);

  // 1. Resolution — try PostHog first, fall back to server.
  useEffect(() => {
    let alive = true;

    const tryPostHog = () => {
      if (!isPostHogEnabled) return false;
      const ph = getInstance();
      if (!ph) return false;
      try {
        const flag = ph.getFeatureFlag(experiment);
        // Treat undefined / false / "control" as "no PostHog override".
        if (flag && flag !== "control") {
          const payload = ph.getFeatureFlagPayload(experiment);
          if (alive) {
            setVariant(String(flag));
            if (payload && typeof payload === "object") {
              setCopy({
                label:    payload.label    || fallback.label,
                headline: payload.headline || fallback.headline,
                cta:      payload.cta      || fallback.cta,
              });
            }
            setResolvedVia("posthog");
            setReady(true);
          }
          return true;
        }
      } catch (_) {
        // Fall through to server resolution.
      }
      return false;
    };

    const tryServer = () => {
      apiClient
        .get(`/nudge/${experiment}?tier=${encodeURIComponent(tier)}`,
             { withCredentials: true })
        .then((r) => {
          if (!alive) return;
          const v = r?.data?.variant;
          const c = r?.data?.copy;
          if (v && c) {
            setVariant(v);
            setCopy({
              label:    c.label    || fallback.label,
              headline: c.headline || fallback.headline,
              cta:      c.cta      || fallback.cta,
            });
          }
          setResolvedVia("server");
          setReady(true);
        })
        .catch(() => {
          if (alive) {
            setResolvedVia("server");
            setReady(true);
          }
        });
    };

    if (isPostHogEnabled) {
      // PostHog flag values may not be loaded yet on first render. Use
      // onFeatureFlags to retry once flags resolve, then fall back.
      const ph = getInstance();
      if (ph) {
        if (!tryPostHog()) {
          let resolved = false;
          try {
            ph.onFeatureFlags(() => {
              if (resolved) return;
              if (tryPostHog()) {
                resolved = true;
              } else {
                resolved = true;
                tryServer();
              }
            });
          } catch (_) {
            tryServer();
          }
        }
      } else {
        tryServer();
      }
    } else {
      tryServer();
    }

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experiment, tier]);

  // 2. Log a single view event once we know the variant.
  useEffect(() => {
    if (viewLoggedRef.current) return;
    if (!ready) return;
    if (!viewWhen) return;
    viewLoggedRef.current = true;

    // Always fire the unified PostHog `experiment_view` event so funnels
    // built in PostHog can attribute regardless of resolution path.
    trackEvent("experiment_view", {
      experiment, variant, tier, source, resolved_via: resolvedVia, ...extraEventFields,
    });

    // Server-side nudge logging stays in place when we resolved via the
    // server path — keeps the legacy /admin/nudge-experiments dashboard
    // populated until PostHog flag-driven dashboards take over.
    if (resolvedVia === "server") {
      apiClient
        .post(`/nudge/${experiment}/event`, {
          variant,
          event_type: "view",
          tier,
          source,
          ...extraEventFields,
        }, { withCredentials: true })
        .catch(() => { /* analytics best-effort */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, variant, viewWhen, resolvedVia]);

  // 3. Click recorder — caller invokes before navigating.
  const recordClick = useCallback(() => {
    trackEvent("experiment_click", {
      experiment, variant, tier, source, resolved_via: resolvedVia, ...extraEventFields,
    });
    if (resolvedVia === "server") {
      apiClient
        .post(`/nudge/${experiment}/event`, {
          variant,
          event_type: "click",
          tier,
          source,
          ...extraEventFields,
        }, { withCredentials: true })
        .catch(() => { /* nav still proceeds */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experiment, variant, tier, source, resolvedVia, JSON.stringify(extraEventFields)]);

  return { variant, copy, ready, recordClick };
}
